import React, { useMemo, useState } from 'react';
import { PRODUCT_TYPES } from '../../constants';
import './KesifMeasurements.css';

// Keşifte alınan ölçüler (müşteri kaydında: kesifItems).
// Müşteri gelmeden oda oda ürün tipi + en/boy girilir; "Siparişe dönüştür" ile sipariş formu bu bilgilerle açılır.

// Boy girilmeyen ürünler ve ölçü girilmeyen ürünler
const WIDTH_ONLY_TYPES = ['raySistemi', 'rustik'];
const NO_SIZE_TYPES = ['aksesuar'];

const emptyForm = (keep = {}) => ({
  productType: keep.productType || '',
  roomName: keep.roomName || '',
  width: '',
  height: '',
  quantity: '',
  notes: ''
});

const formatCm = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num.toLocaleString('tr-TR') : '';
};

const describeKesifSize = (item) => {
  if (NO_SIZE_TYPES.includes(item.productType)) return '';
  const width = formatCm(item.width);
  const height = formatCm(item.height);
  if (WIDTH_ONLY_TYPES.includes(item.productType) || !height) return width ? `${width} cm` : '';
  return width ? `${width} × ${height} cm` : `Boy ${height} cm`;
};

// Akordeon açık/kapalı tercihi bu tarayıcıda hatırlanır
const OPEN_STORAGE_KEY = 'kesifMeasurementsOpen';
const readOpen = () => {
  try { return localStorage.getItem(OPEN_STORAGE_KEY) === '1'; } catch { return false; }
};

const KesifMeasurements = ({ items = [], onSave, onConvert, disabled = false, activeItemId = null }) => {
  const [isOpen, setIsOpen] = useState(readOpen);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const showForm = isFormOpen || items.length === 0;
  const pendingCount = items.filter((item) => !item.convertedOrderId).length;

  // Odalara göre grupla (ekleme sırası korunur)
  const rooms = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = (item.roomName || '').trim() || 'Oda belirtilmedi';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return [...map.entries()];
  }, [items]);

  const roomSuggestions = useMemo(
    () => [...new Set(items.map((item) => (item.roomName || '').trim()).filter(Boolean))],
    [items]
  );

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { productType } = form;
    if (!productType) return setError('Ürün tipini seçin.');
    if (!form.roomName.trim()) return setError('Oda adını yazın.');
    if (!NO_SIZE_TYPES.includes(productType) && !form.width) return setError('En ölçüsünü girin.');

    const size = NO_SIZE_TYPES.includes(productType)
      ? { width: '', height: '' }
      : { width: form.width, height: WIDTH_ONLY_TYPES.includes(productType) ? '' : form.height };
    const fields = {
      productType,
      roomName: form.roomName.trim(),
      ...size,
      quantity: form.quantity,
      notes: form.notes.trim()
    };

    const nextItems = editingId
      ? items.map((item) => (item.id === editingId ? { ...item, ...fields } : item))
      : [...items, { id: `k_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, createdAt: new Date().toISOString(), ...fields }];

    setIsSaving(true);
    const ok = await onSave(nextItems);
    setIsSaving(false);
    if (!ok) return;

    if (editingId) {
      closeForm();
    } else {
      // Aynı odada art arda ölçü girmek için tip ve oda korunur
      setForm(emptyForm({ productType, roomName: fields.roomName }));
      setIsFormOpen(true);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      productType: item.productType || '',
      roomName: item.roomName || '',
      width: item.width || '',
      height: item.height || '',
      quantity: item.quantity || '',
      notes: item.notes || ''
    });
    setIsFormOpen(true);
    setError('');
  };

  const handleDelete = async (id) => {
    setIsSaving(true);
    const ok = await onSave(items.filter((item) => item.id !== id));
    setIsSaving(false);
    if (ok) {
      setDeleteId(null);
      if (editingId === id) closeForm();
    }
  };

  const isBusy = disabled || isSaving;
  const hideHeight = WIDTH_ONLY_TYPES.includes(form.productType);
  const hideSize = NO_SIZE_TYPES.includes(form.productType);

  const toggleOpen = (next = !isOpen) => {
    setIsOpen(next);
    try { localStorage.setItem(OPEN_STORAGE_KEY, next ? '1' : '0'); } catch { /* yoksay */ }
  };

  const summary = items.length === 0
    ? 'Henüz ölçü yok'
    : `${rooms.length} oda · ${items.length} ölçü`;

  return (
    <section className={`km${isOpen ? ' is-open' : ''}`}>
      <div className="km-head">
        <button
          type="button"
          className="km-toggle"
          onClick={() => toggleOpen()}
          aria-expanded={isOpen}
          aria-controls="km-body"
        >
          <span className="km-title-icon" aria-hidden="true">📐</span>
          <span className="km-toggle-text">
            <span className="km-title">
              Keşif ölçüleri
              {items.length > 0 && (
                <span className={`km-count${pendingCount === 0 ? ' is-done' : ''}`}>
                  {pendingCount === 0 ? 'Hepsi siparişte' : `${pendingCount} bekliyor`}
                </span>
              )}
            </span>
            <span className="km-summary">{summary}</span>
          </span>
          <span className="km-chevron" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </span>
        </button>
        {(!isOpen || (items.length > 0 && !showForm)) && (
          <button
            type="button"
            className="km-add-btn"
            onClick={() => { toggleOpen(true); setIsFormOpen(true); }}
            disabled={isBusy}
          >
            + Ölçü ekle
          </button>
        )}
      </div>

      {isOpen && (
      <div className="km-body" id="km-body">
      <p className="km-sub">Müşteri gelmeden oda oda ürün ve ölçüleri girin, sonra tek tıkla siparişe dönüştürün.</p>

      {showForm && (
        <form className="km-form" onSubmit={handleSubmit}>
          <div className="km-field">
            <span className="km-label">Ürün tipi</span>
            <div className="km-type-grid" role="radiogroup" aria-label="Ürün tipi">
              {Object.entries(PRODUCT_TYPES).map(([key, type]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={form.productType === key}
                  className={`km-type${form.productType === key ? ' is-active' : ''}`}
                  data-type={key}
                  onClick={() => setField('productType', key)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="km-row">
            <label className="km-field km-field-room">
              <span className="km-label">Oda</span>
              <input
                type="text"
                list="km-room-list"
                value={form.roomName}
                onChange={(e) => setField('roomName', e.target.value)}
                placeholder="Örn. Salon, Yatak odası"
                maxLength={60}
              />
              <datalist id="km-room-list">
                {roomSuggestions.map((room) => <option key={room} value={room} />)}
              </datalist>
            </label>
            {!hideSize && (
              <label className="km-field">
                <span className="km-label">En (cm)</span>
                <input type="number" inputMode="decimal" min="0" value={form.width} onChange={(e) => setField('width', e.target.value)} placeholder="En" />
              </label>
            )}
            {!hideSize && !hideHeight && (
              <label className="km-field">
                <span className="km-label">Boy (cm)</span>
                <input type="number" inputMode="decimal" min="0" value={form.height} onChange={(e) => setField('height', e.target.value)} placeholder="Boy" />
              </label>
            )}
            <label className="km-field km-field-qty">
              <span className="km-label">Adet</span>
              <input type="number" inputMode="numeric" min="1" value={form.quantity} onChange={(e) => setField('quantity', e.target.value)} placeholder="1" />
            </label>
          </div>

          <label className="km-field">
            <span className="km-label">Not <em>(isteğe bağlı)</em></span>
            <input type="text" value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder="Örn. Pencere sağdan açılıyor, kalorifer altında" maxLength={300} />
          </label>

          {error && <p className="km-error" role="alert">{error}</p>}

          <div className="km-form-actions">
            {(items.length > 0 || editingId) && (
              <button type="button" className="km-btn-ghost" onClick={closeForm} disabled={isSaving}>
                {editingId ? 'Vazgeç' : 'Kapat'}
              </button>
            )}
            <button type="submit" className="km-btn-primary" disabled={isBusy}>
              {isSaving ? 'Kaydediliyor…' : editingId ? 'Güncelle' : 'Ölçüyü kaydet'}
            </button>
          </div>
        </form>
      )}

      {rooms.length > 0 && (
        <div className="km-rooms">
          {rooms.map(([room, roomItems]) => (
            <div key={room} className="km-room">
              <div className="km-room-head">
                <span className="km-room-name">{room}</span>
                <span className="km-room-count">{roomItems.length} ürün</span>
              </div>
              <ul className="km-items">
                {roomItems.map((item) => {
                  const isConverted = !!item.convertedOrderId;
                  const size = describeKesifSize(item);
                  return (
                    <li key={item.id} className={`km-item${isConverted ? ' is-converted' : ''}${activeItemId === item.id ? ' is-active' : ''}`}>
                      <div className="km-item-main">
                        <span className="km-chip" data-type={item.productType}>
                          {PRODUCT_TYPES[item.productType]?.label || item.productType}
                        </span>
                        {size && <span className="km-size">{size}</span>}
                        {parseFloat(item.quantity) > 1 && <span className="km-qty">× {item.quantity}</span>}
                        {item.notes && <span className="km-note">{item.notes}</span>}
                      </div>
                      <div className="km-item-actions">
                        {deleteId === item.id ? (
                          <>
                            <span className="km-confirm-text">Silinsin mi?</span>
                            <button type="button" className="km-btn-danger" onClick={() => handleDelete(item.id)} disabled={isBusy}>Sil</button>
                            <button type="button" className="km-btn-ghost" onClick={() => setDeleteId(null)}>Vazgeç</button>
                          </>
                        ) : (
                          <>
                            {isConverted ? (
                              <span className="km-done">✓ Siparişte</span>
                            ) : (
                              <button type="button" className="km-btn-convert" onClick={() => onConvert(item)} disabled={isBusy}>
                                Siparişe dönüştür →
                              </button>
                            )}
                            <button type="button" className="km-icon-btn" onClick={() => handleEdit(item)} disabled={isBusy} aria-label="Düzenle" title="Düzenle">✎</button>
                            <button type="button" className="km-icon-btn is-danger" onClick={() => setDeleteId(item.id)} disabled={isBusy} aria-label="Sil" title="Sil">✕</button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      </div>
      )}
    </section>
  );
};

export default KesifMeasurements;
